import React, { useCallback, useId, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { signOut } from "firebase/auth";

import { auth } from "../../Models/firebaseConfig";
import { Routes } from "../../Routes";
import ErrorMessage from "../../Components/ErrorMessage/ErrorMessage";
import MenuCoursesSkeleton from "./MenuCoursesSkeleton";
import MenuProgressIndicator from "./MenuProgressIndicator";
import SidebarBrandLogo from "./SidebarBrandLogo";
import SidebarIcon from "./SidebarIcon";
import { sidebarIcons } from "./sidebarIcons";
import { useUser } from "../User/UserObject";
import getCourseProgress from "../../util/getCourseProgress";
import { isSidebarCourseItemFocused } from "./menuCourseSelection";
import { shouldShowEducationMenuHint } from "./educationMenuHint";
import type { Course, MenuProps } from "./Menu.types";
import { useMenuEducations } from "./useMenuEducations";

import styles from "./AppShellHeader.module.css";

type AppShellHeaderProps = MenuProps & {
  showAdmin: boolean;
};

function AppShellHeader({
  availableCourses,
  initialCourse,
  onSelectCourse,
  showAdmin,
}: AppShellHeaderProps): React.JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const isMyCertificatesPage = location.pathname === Routes.MyCertificates;
  const isAdminPage =
    location.pathname === Routes.Admin ||
    location.pathname.startsWith(`${Routes.Admin}/`);

  const {
    userCoursesError,
    retryFetchUserCourses,
    showEducationListSkeleton,
    userOngoingEducations,
    userExpiredOngoingEducations,
    userExpiredEducations,
    userCompletedEducations,
  } = useMenuEducations({
    availableCourses,
    selectedCourse: initialCourse,
    onSelectCourse,
  });

  const certificateCourses = useMemo(
    () => [...userExpiredEducations, ...userCompletedEducations],
    [userCompletedEducations, userExpiredEducations],
  );

  const educationSections = useMemo(() => {
    type Section = {
      id: string;
      title: string;
      courses: Course[];
      isCertificateSection?: boolean;
      isExpiredSection?: boolean;
    };
    const sections: Section[] = [];

    if (userOngoingEducations.length > 0) {
      sections.push({
        id: "ongoing",
        title: t("menu.educations"),
        courses: userOngoingEducations,
      });
    }
    if (certificateCourses.length > 0) {
      sections.push({
        id: "certificates",
        title: t("menu.certificate"),
        courses: certificateCourses,
        isCertificateSection: true,
      });
    }
    if (userExpiredOngoingEducations.length > 0) {
      sections.push({
        id: "expired",
        title: t("menu.expiredEducations"),
        courses: userExpiredOngoingEducations,
        isExpiredSection: true,
      });
    }

    return sections;
  }, [
    certificateCourses,
    t,
    userExpiredOngoingEducations,
    userOngoingEducations,
  ]);

  const allListedEducations = useMemo(
    () =>
      educationSections.flatMap((section) => section.courses),
    [educationSections],
  );

  const educationMenuLabel = useMemo(() => {
    if (
      initialCourse &&
      allListedEducations.some((course) => course.ID === initialCourse.ID)
    ) {
      return initialCourse.Title;
    }
    return t("menu.educations");
  }, [allListedEducations, initialCourse, t]);

  const educationMenuActive = useMemo(() => {
    if (!initialCourse) {
      return false;
    }

    return educationSections.some((section) => {
      if (!section.courses.some((course) => course.ID === initialCourse.ID)) {
        return false;
      }
      return isSidebarCourseItemFocused({
        pathname: location.pathname,
        courseId: initialCourse.ID,
        selectedCourseId: initialCourse.ID,
        isCertificateSection: Boolean(section.isCertificateSection),
      });
    });
  }, [educationSections, initialCourse, location.pathname]);

  const showEducationMenu =
    showEducationListSkeleton || educationSections.length > 0;

  /** Course pill only while the user is on an education view; otherwise match other nav tabs. */
  const educationsTriggerIsNav = !educationMenuActive;

  const showEducationMenuHint = shouldShowEducationMenuHint({
    pathname: location.pathname,
    hasSelectedCourse: initialCourse != null,
  });

  const closeMenus = useCallback(() => setOpenMenuId(null), []);
  const toggleMenu = useCallback((menuId: string) => {
    setOpenMenuId((current) => (current === menuId ? null : menuId));
  }, []);

  return (
    <header className={styles.header}>
      <div className={styles.capsule}>
        <SidebarBrandLogo theme="brandBar" />

        <nav className={styles.actions} aria-label={t("menu.primaryNav", "Huvudmeny")}>
          <div className={styles.actionsCluster}>
            {showAdmin && (
              <HeaderNavLink
                to={Routes.Admin}
                label={t("menu.admin")}
                selected={isAdminPage}
              />
            )}
            <HeaderNavLink
              to={Routes.MyCertificates}
              label={t("menu.myCertificates")}
              selected={isMyCertificatesPage}
            />
            {userCoursesError ? (
              <ErrorMessage
                message={userCoursesError}
                onRetry={retryFetchUserCourses}
              />
            ) : (
              showEducationMenu && (
                <HeaderEducationsMenu
                  menuId="educations"
                  openMenuId={openMenuId}
                  onToggle={toggleMenu}
                  onClose={closeMenus}
                  label={
                    educationsTriggerIsNav
                      ? t("menu.myEducations")
                      : educationMenuLabel
                  }
                  sections={educationSections}
                  showSkeleton={showEducationListSkeleton}
                  selectedCourse={initialCourse}
                  onSelectCourse={onSelectCourse}
                  triggerSelected={educationMenuActive}
                  triggerStyle={educationsTriggerIsNav ? "nav" : "course"}
                  navTriggerPassive={educationsTriggerIsNav}
                  showHint={showEducationMenuHint}
                />
              )
            )}
          </div>
          {showEducationMenu && !userCoursesError && (
            <span className={styles.navDivider} aria-hidden="true" />
          )}
          <HeaderUserMenu
            menuId="user"
            openMenuId={openMenuId}
            onToggle={toggleMenu}
            onClose={closeMenus}
          />
        </nav>
      </div>
    </header>
  );
}

type HeaderNavLinkProps = {
  to: string;
  label: string;
  selected: boolean;
  badgeCount?: number;
};

function HeaderNavLink({
  to,
  label,
  selected,
  badgeCount = 0,
}: HeaderNavLinkProps): React.JSX.Element {
  const showBadge = badgeCount > 0;

  return (
    <Link
      to={to}
      className={clsx(styles.navLink, selected && styles.navLinkSelected)}
    >
      <span>{label}</span>
      {showBadge && (
        <span className={styles.navBadge} aria-label={`${badgeCount}`}>
          {badgeCount > 99 ? "99+" : badgeCount}
        </span>
      )}
    </Link>
  );
}

type EducationMenuSection = {
  id: string;
  title: string;
  courses: Course[];
  isCertificateSection?: boolean;
  isExpiredSection?: boolean;
};

type HeaderEducationsMenuProps = {
  menuId: string;
  openMenuId: string | null;
  onToggle: (menuId: string) => void;
  onClose: () => void;
  label: string;
  sections: EducationMenuSection[];
  showSkeleton: boolean;
  selectedCourse: Course | null;
  onSelectCourse: (course: Course) => void;
  triggerSelected?: boolean;
  triggerStyle?: "course" | "nav";
  /** Nav-style trigger: no hover highlight while another primary tab is active */
  navTriggerPassive?: boolean;
  showHint?: boolean;
};

function HeaderEducationsMenu({
  menuId,
  openMenuId,
  onToggle,
  onClose,
  label,
  sections,
  showSkeleton,
  selectedCourse,
  onSelectCourse,
  triggerSelected = false,
  triggerStyle = "course",
  navTriggerPassive = false,
  showHint = false,
}: HeaderEducationsMenuProps): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const listId = useId();
  const open = openMenuId === menuId;

  const onSelectCourseFromSection = useCallback(
    (course: Course, section: EducationMenuSection) => {
      onSelectCourse(course);
      onClose();
      if (section.isCertificateSection) {
        navigate(`${Routes.Educations}/${course.ID}/certifikat`);
        return;
      }
      navigate(Routes.Educations);
    },
    [navigate, onClose, onSelectCourse],
  );

  const isNavTrigger = triggerStyle === "nav";
  return (
    <div className={styles.menuWrap}>
      <button
        type="button"
        className={clsx(
          styles.menuTrigger,
          isNavTrigger ? styles.menuTriggerNav : styles.menuTriggerCourse,
          open && styles.menuTriggerOpen,
          !isNavTrigger &&
            triggerSelected &&
            styles.menuTriggerCourseActive,
          isNavTrigger && open && styles.menuTriggerNavOpen,
          isNavTrigger && navTriggerPassive && styles.menuTriggerNavPassive,
        )}
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="menu"
        onClick={() => onToggle(menuId)}
      >
        <span className={styles.menuTriggerLabel}>{label}</span>
        <SidebarIcon
          icon={sidebarIcons.headerCourseMenu}
          size="sm"
          className={clsx(
            styles.menuChevron,
            isNavTrigger ? styles.menuChevronNav : styles.menuChevronCourse,
            open && styles.menuChevronOpen,
          )}
        />
      </button>
      {showHint && !open && (
        <EducationMenuHint label={t("welcome.arrowText")} />
      )}
      {open && (
        <>
          <button
            type="button"
            className={styles.menuBackdrop}
            aria-label="Stäng"
            onClick={onClose}
          />
          <div
            id={listId}
            className={styles.menuPanel}
          >
            {showSkeleton ? (
              <MenuCoursesSkeleton rows={4} />
            ) : (
              sections.map((section, sectionIndex) => (
                <section
                  key={section.id}
                  className={clsx(
                    styles.menuSection,
                    sectionIndex > 0 && styles.menuSectionDivider,
                  )}
                  aria-label={section.title}
                >
                  <h3 className={styles.menuSectionHeading}>{section.title}</h3>
                  <ul className={styles.menuSectionList} role="menu">
                    {section.courses.map((course) => (
                      <HeaderEducationMenuItem
                        key={`${section.id}-${course.ID}`}
                        course={course}
                        section={section}
                        selectedCourse={selectedCourse}
                        pathname={location.pathname}
                        user={user}
                        onSelect={() =>
                          onSelectCourseFromSection(course, section)
                        }
                      />
                    ))}
                  </ul>
                </section>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

function EducationMenuHint({ label }: { label: string }): React.JSX.Element {
  return (
    <div className={styles.menuHint} aria-hidden="true">
      <svg
        className={styles.menuHintArrow}
        viewBox="0 0 24 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 36 V6 M4 14 L12 4 L20 14"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className={styles.menuHintText}>{label}</span>
    </div>
  );
}

type HeaderEducationMenuItemProps = {
  course: Course;
  section: EducationMenuSection;
  selectedCourse: Course | null;
  pathname: string;
  user: ReturnType<typeof useUser>["user"];
  onSelect: () => void;
};

function HeaderEducationMenuItem({
  course,
  section,
  selectedCourse,
  pathname,
  user,
  onSelect,
}: HeaderEducationMenuItemProps): React.JSX.Element {
  const progress = getCourseProgress(course, user);
  const focused = isSidebarCourseItemFocused({
    pathname,
    courseId: course.ID,
    selectedCourseId: selectedCourse?.ID,
    isCertificateSection: Boolean(section.isCertificateSection),
  });
  const progressLabel =
    progress == null
      ? null
      : (() => {
          const clamped = Math.min(100, Math.max(0, Math.round(progress)));
          if (clamped >= 100) {
            return null;
          }
          if (clamped <= 0) {
            return "Ny";
          }
          return `${clamped}%`;
        })();

  return (
    <li role="none">
      <button
        type="button"
        role="menuitem"
        className={clsx(
          styles.menuItem,
          focused && styles.menuItemSelected,
          section.isExpiredSection && styles.menuItemMuted,
        )}
        onClick={onSelect}
      >
        {progress != null && (
          <MenuProgressIndicator
            progress={progress}
            expired={section.isExpiredSection}
            completedAsBrandDot={section.isCertificateSection}
            selected={focused}
          />
        )}
        <span className={styles.menuItemText}>{course.Title}</span>
        {progressLabel != null && (
          <span className={styles.menuItemMeta}>{progressLabel}</span>
        )}
      </button>
    </li>
  );
}

type HeaderUserMenuProps = {
  menuId: string;
  openMenuId: string | null;
  onToggle: (menuId: string) => void;
  onClose: () => void;
};

function HeaderUserMenu({
  menuId,
  openMenuId,
  onToggle,
  onClose,
}: HeaderUserMenuProps): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const listId = useId();
  const open = openMenuId === menuId;

  const displayName =
    [user?.Firstname, user?.Lastname].filter(Boolean).join(" ").trim() ||
    user?.Email ||
    t("menu.account");

  const onLogout = useCallback(() => {
    signOut(auth)
      .then(() => {
        navigate(Routes.Home);
      })
      .catch((error) => {
        console.error("Error signing out: ", error);
      });
  }, [navigate]);

  return (
    <div className={clsx(styles.menuWrap, styles.userMenuWrap)}>
      <button
        type="button"
        className={clsx(
          styles.menuTrigger,
          styles.userTrigger,
          open && styles.menuTriggerOpen,
        )}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => onToggle(menuId)}
      >
        <span className={styles.userAvatar} aria-hidden="true">
          <SidebarIcon icon={sidebarIcons.user} />
        </span>
        <span className={styles.userName}>{displayName}</span>
      </button>
      {open && (
        <>
          <button
            type="button"
            className={styles.menuBackdrop}
            aria-label="Stäng"
            onClick={onClose}
          />
          <ul
            id={listId}
            className={clsx(styles.menuPanel, styles.userMenuPanel)}
            role="menu"
          >
            <li role="none">
              <Link
                to={Routes.Account}
                role="menuitem"
                className={styles.userMenuItem}
                onClick={onClose}
              >
                <SidebarIcon
                  icon={sidebarIcons.user}
                  size="sm"
                  className={styles.userMenuItemIcon}
                />
                {t("menu.account")}
              </Link>
            </li>
            <li role="none">
              <button
                type="button"
                role="menuitem"
                className={styles.userMenuItem}
                onClick={onLogout}
              >
                <SidebarIcon
                  icon={sidebarIcons.logout}
                  size="sm"
                  className={styles.userMenuItemIcon}
                />
                {t("account.logout")}
              </button>
            </li>
          </ul>
        </>
      )}
    </div>
  );
}

export default AppShellHeader;
